<template>
  <div>
    <div v-if="verifiedUser" key="app">
      <HeaderBar :showBar="true"></HeaderBar>
      <Vendor v-if="isVendor" @initialized="initialized = true" />
      <Agency v-if="!isVendor" @initialized="initialized = true" />
    </div>
    <div v-if="production">
      <div v-if="!initialized" key="loading">
        <Logo />
      </div>
    </div>
    <div v-else>
      <div v-if="!initialized" key="loading">
        <Logo v-if="authenticated" />
        <Login v-else />
      </div>
    </div>
    <transition name="slide-fade">
      <SuccessMessage v-if="userMessage" :data="userMessage" />
    </transition>
  </div>
</template>

<script>
import SuccessMessage from '@/components/SuccessMessage';
import HeaderBar from '@/components/DesignSystem/HeaderBar';
import Vendor from '@/apps/vendorDashboard/VendorDashboard';
import Agency from '@/apps/agencyDashboard/AgencyDashboard';
import Logo from '@/components/SpinningLogo';
import Login from '@/views/Login';
import { apiService } from '@/utils/api/index';
import ActivityBasedTimer from '@/utils/activityBasedTimer';
import VersionCheck from '@/utils/versionCheck';
import { mapMutations, mapGetters } from 'vuex';

export default {
  components: {
    Vendor,
    Agency,
    Logo,
    SuccessMessage,
    Login,
    HeaderBar,
  },

  data() {
    return {
      verifiedUser: false,
      initialized: false,
      isVendor: false,
      authenticated: true,
    };
  },
  computed: {
    ...mapGetters(['userMessage']),

    production() {
      return process.env.NODE_ENV === 'production';
    },
  },
  async created() {
    if (this.$route.query.accessToken && this.$route.query.idToken) {
      this.$auth.token = this.$route.query.idToken;
      this.$auth.accessToken = this.$route.query.accessToken;
      this.$auth.expiresAt = 60 * 60 * 24;
    }

    await this.$auth.handleAuthentication();

    if (process.env.NODE_ENV === 'production') {
      if (!this.$auth.isAuthenticated()) {
        localStorage.setItem('redirectPath', window.location.pathname);
        return this.$auth.login();
      }
    } else {
      if (!this.$auth.isAuthenticated()) {
        this.authenticated = false;
        return localStorage.setItem('redirectPath', window.location.pathname);
      }
    }

    const authTimer = new ActivityBasedTimer();
    const versioningTimer = new ActivityBasedTimer();
    const versionCheck = new VersionCheck();

    const showUpdate = this.showUpdate;
    const auth = this.$auth;

    versioningTimer.setInterval({
      async callback() {
        const newVersionAvailable = await versionCheck.isUpdateAvailable();

        if (newVersionAvailable) {
          showUpdate();
        }
      },
      interval: 1000 * 60 * 10,
      forcedInterval: 1000 * 60 * 60 * 24,
    });

    authTimer.setInterval({
      async callback() {
        auth.checkSession();
      },
      interval: 1000 * 60 * 100,
      forcedInterval: 1000 * 60 * 60 * 24,
    });

    this.$router.beforeEach((to, from, next) => {
      authTimer.runTimersCheck();
      versioningTimer.runTimersCheck();
      next();
    });

    apiService.init(process.env.VUE_APP_ROOT_API);
    apiService.setHeader();

    this.checkUser();
    if (process.env.NODE_ENV !== 'development') {
      this.freshpaintIdentify();
    }
  },

  methods: {
    ...mapMutations(['setUser']),

    async checkUser() {
      try {
        const data = await apiService.get('/users/me/');

        // If a user is not associated with neither a vendor or an agency
        if (!data.reseller && !data.vendor) {
          this.$store.dispatch('showUserMessage', {
            success: false,
            persist: true,
            title: 'Something went wrong!',
            message:
              'It looks like your account is not associated with an agency or vendor. Please contact PartnerPage to get this issue resolved.',
          });
          return;
        }

        this.setUser(data);
        let redirectPath = localStorage.getItem('redirectPath');
        localStorage.removeItem('redirectPath');

        if (data.isVendor) {
          this.isVendor = true;
          this.verifiedUser = true;
          if (redirectPath) {
            this.$router.push(redirectPath);
          }
          return;
        }

        this.verifiedUser = true;

        // If user is refreshing page on agency dashboard
        if (!redirectPath) {
          if (window.location.pathname === '/') {
            return this.$router.push('/agency/our-public-profiles');
          }
          return;
        }

        if (redirectPath === '/') {
          redirectPath = '/agency/our-public-profiles';
        }

        this.$router.push(redirectPath);
      } catch (e) {
        this.$store.dispatch('showUserMessage', {
          success: false,
          persist: true,
          title: 'Something went wrong!',
          message:
            'Please make sure you have a PartnerPage account and that you are logging in with the correct credentials.',
        });
      }
    },

    async freshpaintIdentify() {
      try {
        const analytics = await apiService.get('/users/freshpaint-analytics/');
        //Freshpaint set up user properties
        var profile = {
          about_us: null,
          user_id: analytics.id,
          is_superuser: analytics.isSuperuser,
          first_name: analytics.firstName,
          last_name: analytics.lastName,
          slug: analytics.slug,
          email: analytics.email,
          email_confirmed: analytics.emailConfirmed,
          is_staff: analytics.isStaff,
          is_active: analytics.isActive,
          created_at: analytics.dateJoined,
          bio: analytics.bio,
          organization_name: analytics.organizationName,
          avatar: analytics.avatar,
          auth0_id: analytics.auth0Id,
          is_social: analytics.isSocial,
          logo: analytics.logo,
          partner_page_url: analytics.partnerpageUrl,
          custom_domain: analytics.customDomain,
          organization_type: analytics.organizationType,
          partner_program_url: analytics.partnerProgramUrl,
          terms_of_service_url: analytics.terms_url,
          privacy_policy_url: analytics.privacyUrl,
          leads_total: analytics.leadCount.total,
          leads_pending: analytics.leadCount.pending,
          leads_accepted: analytics.leadCount.accepted,
          leads_declined: analytics.leadCount.declined,
          review_count: analytics.reviewCount,
          partner_count: analytics.partnerCount,
          env: process.env.NODE_ENV,
          public_profiles: analytics.publicProfiles,
          active_public_profiles: analytics.activePublicProfiles,
        };
        (profile['invited_by_organization'] = analytics.invitedBy.invitedByOrg),
          (profile['invited_by_first_name'] =
            analytics.invitedBy.invitedByFirstName),
          (profile['invited_by_last_name'] =
            analytics.invitedBy.invitedByLastName),
          window.freshpaint.identify(analytics.email, profile);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },

    showUpdate() {
      this.$store.dispatch('showUserMessage', {
        info: true,
        persist: true,
        hideClose: true,
        title: 'Update Available',
        message: 'Click to update PartnerPage',
        action: () => {
          window.location.reload();
        },
      });
    },
  },
};
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.75s ease;
}
.slide-fade-leave-active {
  transition: all 0.75s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(50px);
  opacity: 0;
}
</style>
